import { useQuery } from '@tanstack/react-query';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mlm/ui';
import { listBranches, type Branch } from '../lib/api';
import { useAuthStore, useIsSuperAdmin } from '../lib/auth-store';

// Radix's Select can't hold an empty string as a value, so "no filter" rides a
// sentinel that is mapped back to undefined at the component boundary.
const ALL = '__all';

/**
 * The branch list, shared by every picker on the page — one network call no
 * matter how many render.
 *
 * NOTE: `GET /branches` is unauthenticated today (no preHandler on the route).
 * Out of scope here; flagged so nobody assumes the picker is what protects it.
 */
export function useBranches() {
  return useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
    staleTime: 5 * 60_000,
  });
}

/**
 * True when this admin is confined to one branch by their role scope. The
 * server pins their results regardless, so an interactive picker would only be
 * a lie — see `scopedFallback` for what renders instead.
 */
export function useIsBranchScoped(): boolean {
  const scoped = useAuthStore((s) => !!s.user?.branchId);
  return scoped && !useIsSuperAdmin();
}

const label = (b: Branch) => `${b.name} (${b.code})`;

/** The branch a scoped operator is pinned to, resolved to a display label. */
export function useScopedBranchLabel(): string | null {
  const branchId = useAuthStore((s) => s.user?.branchId ?? null);
  const { data: branches } = useBranches();
  if (!branchId) return null;
  const b = (branches ?? []).find((x) => x.id === branchId);
  return b ? label(b) : null;
}

/** Single-select branch filter — the Reports filter row and the dashboard bar. */
export function BranchPicker({
  value,
  onChange,
  label: fieldLabel = 'Branch',
  allLabel = 'All branches',
  scopedFallback = 'hidden',
}: {
  value: string | undefined;
  onChange: (branchId: string | undefined) => void;
  label?: string;
  allLabel?: string;
  /**
   * What a branch-scoped operator sees. 'hidden' (default) renders nothing —
   * the long-standing Reports behaviour. 'label' renders a static, read-only
   * badge naming their branch: on the dashboard the numbers are already pinned
   * and several cards carry an "All branches" marker, so showing no cause at
   * all is more confusing than showing an uneditable one.
   */
  scopedFallback?: 'hidden' | 'label';
}) {
  const { data: branches } = useBranches();
  const scoped = useIsBranchScoped();
  const scopedLabel = useScopedBranchLabel();
  if (scoped) {
    if (scopedFallback === 'hidden') return null;
    return (
      <div>
        <p className="text-xs text-muted-foreground mb-1">{fieldLabel}</p>
        <div className="flex h-10 items-center rounded-md border border-dashed bg-muted/40 px-3 text-sm text-muted-foreground">
          {scopedLabel ?? 'Your branch'}
        </div>
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{fieldLabel}</p>
      <Select
        value={value ?? ALL}
        onValueChange={(v) => onChange(v === ALL ? undefined : v)}
      >
        <SelectTrigger className="h-10 min-w-[190px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{allLabel}</SelectItem>
          {(branches ?? []).map((b) => (
            <SelectItem key={b.id} value={b.id}>
              {label(b)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// NOTE: `BranchCheckboxList` (multi-select, localStorage-persisted) lived here
// for the dashboard's Sales-per-Branch card. It was removed when the dashboard
// grew a single page-level branch filter — two branch controls on one page
// contradict each other, and the card now narrows server-side like every other
// widget. Its orphaned key `mlm.admin.dashboard.salesByBranch.selected` is left
// in operators' localStorage: nothing reads it, and a cleanup side effect would
// outlive its usefulness.
